import { useEffect, useMemo } from 'react';

import { useTitlesApi } from '@api/hooks/useTitlesApi';
import useLoad from '@hooks/useLoad';
import useMetadataContext from '@hooks/useMetadataContext';
import useSearchContext from '@hooks/useSearchContext';
import { useIsNewIngest } from '@hooks/useTitlesDataSource';
import { SearchQueryDTO } from '@models/SearchQuery';
import SearchResults from '@models/SearchResults';
import { SimpleTitle } from '@models/Title';
import { getFilterWithProvider } from '@utils/filterUtils';

import { SearchAndFilterValues } from './context';
import { buildOrderByStr, getPageCount, maxSkip } from './utils';

const empty: SearchResults<SimpleTitle> = { data: [], facets: {}, totalCount: 0 };

const useTitleSearch = (
  {
    filterCount,
    filterDefLoad: { data: filterDefs = [] },
    filterTrigger,
    pageNo,
    pageSize,
    provider,
    searchQuery,
    searchQuery: { queryText, orderBy, filter, indexName },
    setPageNo,
  }: Omit<SearchAndFilterValues, 'queryResult'>,
  disabled?: boolean
) => {
  const { searchTitles } = useTitlesApi();
  const { country, searchLanguage } = useSearchContext();
  const { setSearchResults } = useMetadataContext();
  const isNewIngest = useIsNewIngest();

  const orderByStr = buildOrderByStr(orderBy);

  const disableSearch = disabled || (!queryText && !filterCount && !provider);

  const skip = Math.min((pageNo - 1) * pageSize, maxSkip);

  const filterWithProvider = useMemo(
    () => getFilterWithProvider(filterDefs, filter, provider),
    [filterDefs, filter, provider]
  );

  const searchQueryDTO: SearchQueryDTO = {
    ...searchQuery,
    orderBy: orderByStr,
    take: pageSize,
    filter: filterWithProvider,
    ...(pageNo !== 0 ? { skip } : {}),
  };

  const queryResult = useLoad(
    () => searchTitles(searchQueryDTO, indexName),
    [
      'search-titles',
      country,
      filterTrigger,
      JSON.stringify(searchQuery),
      JSON.stringify(searchQueryDTO),
      pageNo,
      isNewIngest,
      searchLanguage,
    ],
    { enabled: !disableSearch }
  );

  const { data: loadData, isLoading, error } = queryResult;

  useEffect(() => {
    if (loadData?.totalCount) {
      const lastValidPage = getPageCount(loadData?.totalCount, pageSize);
      if (pageNo > lastValidPage) setPageNo(lastValidPage, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadData?.totalCount]);

  useEffect(() => {
    if (queryResult.data && queryResult.data?.totalCount > 0)
      setSearchResults(queryResult.data.data);
    else if (queryText === '') setSearchResults([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryResult.data, queryText]);

  return {
    titles: disableSearch ? empty : (loadData ?? empty),
    isLoading,
    error,
  };
};

export default useTitleSearch;
